import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import { type Team } from '@/features/iam/teams/Team'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { mapBillingMethodToDisplayData } from '@/features/billing/payment-methods/billing-method-display'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { type DeleteBillingMethodDialogViewModel } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogViewModel'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * Options for {@link BillingMethodListItemViewModel}.
 */
export interface BillingMethodListItemViewModelOptions {
  /**
   * The team whose default the method is made, instead of the current
   * user's.
   */
  team?: Team | null
  /**
   * Selects the method, for lists that pick a method.
   */
  select?: (method: BillingMethod) => Promise<unknown>
  /**
   * Whether the method is the picked one, for lists that pick a method;
   * otherwise the default method is.
   */
  isSelected?: (method: BillingMethod) => boolean
  /**
   * Whether the method is being picked, for lists that pick a method.
   */
  isSelecting?: (method: BillingMethod) => boolean
}

/**
 * View model for a billing method item.
 */
export class BillingMethodListItemViewModel {
  constructor(
    public readonly method: BillingMethod,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _deleteDialogVm: DeleteBillingMethodDialogViewModel,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _options: BillingMethodListItemViewModelOptions = {},
  ) {}

  /**
   * The method's ID.
   */
  @computed
  get id() {
    return this.method.id
  }

  /**
   * Whether the method is the profile's default.
   */
  @computed
  get isDefault() {
    return this.method.isDefault
  }

  /**
   * Whether the method shows as the current one.
   */
  @computed
  get isSelected() {
    return this._options.isSelected
      ? this._options.isSelected(this.method)
      : this.isDefault
  }

  /**
   * What the list shows about the method.
   */
  @computed
  get displayData() {
    return mapBillingMethodToDisplayData(this.method)
  }

  /**
   * Whether an action on the method is in progress.
   */
  @computed
  get isBusy() {
    return (
      this.setAsDefault.pending ||
      Boolean(this._options.isSelecting?.(this.method)) ||
      this._deleteDialogVm.isDeleting(this.method)
    )
  }

  /**
   * Makes the method the default.
   */
  setAsDefault = task.resolved(async () => {
    await this._billingProfileStore
      .changeDefaultBillingMethod(this.method, this._options.team)
      .catch(
        this._createAlertErrorHandler(
          'Changing the default payment method failed',
        ),
      )
  })

  /**
   * Selects the method.
   */
  select = task.resolved(async () => {
    await this._options.select?.(this.method)
  })

  /**
   * Deletes the method after the user confirms.
   */
  @action.bound
  delete() {
    return this._deleteDialogVm.prompt(this.method)
  }
}
